import {
  INestedFieldEntity,
  INestedFieldState,
  ISelectedEntity,
  IValidatedNestedField,
} from './types';
import { DraftFunction, useImmer } from 'use-immer';
import { freeze } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { cloneDeep, isNil } from 'lodash';
import { IFieldFieldsFragment } from '../../../utils/apollo/graphqlSchema';

const getFieldKey = (
  nestedFieldEntity: INestedFieldEntity | IFieldFieldsFragment,
) => {
  switch (nestedFieldEntity.__typename) {
    case 'Field':
      return nestedFieldEntity.key;
    case '__NestedFieldEntity':
      return nestedFieldEntity.field.key;
  }
};

const EMPTY_STATE: INestedFieldState = freeze({
  key: null,
  type: null,
  error: null,
});

const extractNestedFields = (
  selectedEntities: ISelectedEntity[],
): Record<string, INestedFieldState> => {
  const result: Record<string, INestedFieldState> = {};
  for (const entity of selectedEntities) {
    if (entity.__typename === 'Field' && entity.nestedFieldMetadata) {
      const { type } = entity;
      const { fieldKey, key } = entity.nestedFieldMetadata;
      result[fieldKey] = {
        key,
        type,
        error: null,
      };
      result[entity.key] = {
        key,
        type,
        error: null,
      };
    }
  }
  return result;
};

export const useNestedFieldState = (selectedEntities: ISelectedEntity[]) => {
  const [nestedFieldState, setNestedFieldState] = useImmer<
    Record<string, INestedFieldState>
  >(extractNestedFields(selectedEntities));

  const getState = useCallback(
    (nestedFieldEntity: INestedFieldEntity | IFieldFieldsFragment) => {
      const key = getFieldKey(nestedFieldEntity);
      return nestedFieldState[key] ?? EMPTY_STATE;
    },
    [nestedFieldState],
  );

  const getValidatedNestedField = useCallback(
    (
      nestedFieldEntity: INestedFieldEntity | IFieldFieldsFragment,
    ): IValidatedNestedField | null => {
      const { key, type } = getState(nestedFieldEntity);
      if (!isNil(key) && !isNil(type)) {
        return { key, type, fieldKey: getFieldKey(nestedFieldEntity) };
      } else {
        return null;
      }
    },
    [getState],
  );

  const setState = useCallback(
    (
      nestedFieldEntity: INestedFieldEntity | IFieldFieldsFragment,
      updater: DraftFunction<INestedFieldState>,
    ) => {
      setNestedFieldState((draft) => {
        const key = getFieldKey(nestedFieldEntity);
        if (!(key in draft)) {
          draft[key] = cloneDeep(EMPTY_STATE);
        }
        updater(draft[key]);
      });
    },
    [setNestedFieldState],
  );

  return {
    getNestedFieldState: getState,
    setNestedFieldState: setState,
    getValidatedNestedField,
  };
};
